import { Box, Group, Stack, Text, Title, useMantineTheme } from '@mantine/core';

import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ModificationTag } from '../../../../shared/components/UI/MidificationTag/ModificationTag';

import type { Rectification } from '../../../../shared/entities/Rectification';
import {
  formatDateAsText,
  formatDateInParisTZ,
} from '../../../../shared/utils/dates';
import { useNoticeContext } from '../../contexts/Notice.provider';

export function RectificationsTab() {
  return (
    <Stack
      p="05"
      mb="05"
      sx={theme => ({
        border: `1px solid ${theme.colors.gray[1]}`,
        borderRadius: theme.radius.md,
        boxShadow: theme.shadows.xs,
      })}
    >
      <Header />
      <Rectifications />
    </Stack>
  );
}

function Header() {
  const { t } = useTranslation('notice');
  const { tender } = useNoticeContext();

  return (
    <Stack spacing="01">
      <Group noWrap spacing="02">
        <Title order={5}>{t('documents.rectifications.title')}</Title>
        <Text c="gray.3">&bull;</Text>
        <Text size="sm" fw={400} c="gray.6">
          {tender.rectifications.length}
        </Text>
      </Group>
      <Text size="sm" fw={400} c="gray.6">
        {t('documents.rectifications.subtitle', {
          buyerName: tender.buyerName,
        })}
      </Text>
    </Stack>
  );
}

function Rectifications() {
  const { tender } = useNoticeContext();

  return (
    <Stack spacing={0}>
      {tender.rectifications.map(rectification => (
        <RectificationComponent
          key={rectification.changesDescription}
          rectification={rectification}
        />
      ))}
    </Stack>
  );
}

type RectificationProps = {
  rectification: Rectification;
};

function RectificationComponent({ rectification }: RectificationProps) {
  const { t } = useTranslation(['notice', 'domain']);
  const theme = useMantineTheme();

  return (
    <Stack spacing={0}>
      <Group noWrap spacing="03">
        <Box
          w="04"
          h="04"
          sx={theme => ({
            borderRadius: '100%',
            border: `4px solid ${theme.colors.primary[2]}`,
          })}
        />
        <Text size="sm" fw={400} c="gray.6">
          {formatDateAsText({
            date: rectification.publishedAt,
            truncateMonth: false,
          })}
        </Text>
      </Group>
      <Box
        ml="7px"
        pl="03"
        pt="02"
        pb="05"
        sx={theme => ({
          borderLeft: `2px dotted ${theme.colors.primary[2]}`,
        })}
      >
        <Stack spacing="01" ml="02">
          <ModificationTag
            content={t('documents.rectifications.modification')}
          />
          <Text size="sm" fw={400} c="gray.9">
            {rectification.changesDescription}
          </Text>
          <Text variant="xs" color="gray.4">
            <Trans
              ns="notice"
              i18nKey="documents.timeLineLabels.rectifications_withdate"
              components={{
                rectificationLink: (
                  <Link
                    to={`https://www.boamp.fr/pages/avis/?q=idweb:"${rectification.boampId}"`}
                    target="_blank"
                    style={{
                      color: theme.colors.primary[6],
                      textDecoration: 'underline',
                    }}
                  >
                    {t('domain:rectification')}
                  </Link>
                ),
              }}
              values={{
                date: formatDateInParisTZ(rectification.publishedAt),
              }}
            />
          </Text>
        </Stack>
      </Box>
    </Stack>
  );
}
